import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styles from "./Navigation.module.css";
import SearchModal from "./SearchModal/SearchModal";
import logo from "../assets/images/logo.png";

const Navigation = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [companyOpen, setCompanyOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const location = useLocation();
    const currentPath = location.pathname;

    const toggleServices = () => setServicesOpen(!servicesOpen);
    const toggleCompany = () => setCompanyOpen(!companyOpen);

    const closeMenu = () => {
        setIsMenuOpen(false);
        setServicesOpen(false);
        setCompanyOpen(false);
    };

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isMenuOpen]);

    // Helper function to check if path is active
    const isActive = (path) => currentPath === path;
    const isServicesActive = () => currentPath.startsWith("/services");
    const isCompanyActive = () => ["/about", "/contact", "/legal"].includes(currentPath);

    return (
        <nav className={styles.navbar}>
            <div className={styles.navContainer}>
                {/* Logo */}
                <div className={styles.logo}>
                    <Link to="/" onClick={closeMenu}>
                        {/* <span className={styles.logoText}>Only Care</span>
            <span className={styles.logoSubtext}>Home Health</span> */}
                        <img src={logo} alt="Only Care Home Health Logo" className={styles.logoImage} />
                    </Link>
                </div>

                {/* Mobile Actions */}
                <div className={styles.mobileActions}>
                    <button className={styles.mobileSearchBtn} aria-label="Search" onClick={() => setSearchOpen(true)}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" />
                            <path d="M12 12l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>

                    <button
                        className={styles.mobileToggle}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label="Toggle menu"
                        aria-expanded={isMenuOpen}
                    >
                        {isMenuOpen ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Navigation Menu */}
                <div className={`${styles.navMenu} ${isMenuOpen ? styles.active : ""}`}>
                    <ul className={styles.navList}>
                        <li className={styles.navItem}>
                            <Link to="/" className={`${styles.navLink} ${isActive("/") ? styles.active : ""}`} onClick={closeMenu}>
                                Home
                            </Link>
                        </li>

                        {/* Services Dropdown */}
                        <li
                            className={`${styles.navItem} ${styles.dropdown}`}
                            onMouseEnter={() => window.innerWidth > 992 && setServicesOpen(true)}
                            onMouseLeave={() => window.innerWidth > 992 && setServicesOpen(false)}
                        >
                            <button className={`${styles.navLink} ${isServicesActive() ? styles.active : ""}`} onClick={toggleServices}>
                                Services
                                <svg
                                    width="12"
                                    height="8"
                                    viewBox="0 0 12 8"
                                    className={styles.dropdownIcon}
                                    style={{ transform: servicesOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                                >
                                    <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none" />
                                </svg>
                            </button>
                            <ul className={`${styles.dropdownMenu} ${servicesOpen ? styles.show : ""}`}>
                                <li>
                                    <Link to="/services/skilled-nursing" onClick={closeMenu}>
                                        Skilled Nursing
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/physical-therapy" onClick={closeMenu}>
                                        Physical Therapy
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/occupational-therapy" onClick={closeMenu}>
                                        Occupational Therapy
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/speech-therapy" onClick={closeMenu}>
                                        Speech Therapy
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/social-services" onClick={closeMenu}>
                                        Social Services
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/certified-home-health-aid" onClick={closeMenu}>
                                        Certified Home Health Aid
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/alzheimers-care" onClick={closeMenu}>
                                        Alzheimer's Care
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/geriatric-care-management" onClick={closeMenu}>
                                        Geriatric Care Management
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/medication-reminders" onClick={closeMenu}>
                                        Medication Reminders
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/services/post-hospitalization-care" onClick={closeMenu}>
                                        Post-hospitalization Care and Support after General Surgery
                                    </Link>
                                </li>
                                <li className={styles.viewAll}>
                                    <Link to="/services" onClick={closeMenu}>
                                        View All Services
                                    </Link>
                                </li>
                            </ul>
                        </li>

                        <li className={styles.navItem}>
                            <Link to="/reviews" className={`${styles.navLink} ${isActive("/reviews") ? styles.active : ""}`} onClick={closeMenu}>
                                Reviews
                            </Link>
                        </li>

                        {/* Company Dropdown */}
                        <li
                            className={`${styles.navItem} ${styles.dropdown}`}
                            onMouseEnter={() => window.innerWidth > 992 && setCompanyOpen(true)}
                            onMouseLeave={() => window.innerWidth > 992 && setCompanyOpen(false)}
                        >
                            <button className={`${styles.navLink} ${isCompanyActive() ? styles.active : ""}`} onClick={toggleCompany}>
                                Company
                                <svg
                                    width="12"
                                    height="8"
                                    viewBox="0 0 12 8"
                                    className={styles.dropdownIcon}
                                    style={{ transform: companyOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                                >
                                    <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none" />
                                </svg>
                            </button>
                            <ul className={`${styles.dropdownMenu} ${companyOpen ? styles.show : ""}`}>
                                <li>
                                    <Link to="/about" onClick={closeMenu}>
                                        About
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/contact" onClick={closeMenu}>
                                        Contact Us
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/legal" onClick={closeMenu}>
                                        Legal
                                    </Link>
                                </li>
                            </ul>
                        </li>

                        <li className={styles.navItem}>
                            <Link to="/counties" className={`${styles.navLink} ${isActive("/counties") ? styles.active : ""}`} onClick={closeMenu}>
                                Counties
                            </Link>
                        </li>

                        <li className={styles.navItem}>
                            <Link
                                to="/learn-more"
                                className={`${styles.navLink} ${isActive("/learn-more") ? styles.active : ""}`}
                                onClick={closeMenu}
                            >
                                Learn More
                            </Link>
                        </li>
                    </ul>

                    {/* Search and CTA */}
                    <div className={styles.navActions}>
                        <button className={styles.searchBtn} aria-label="Search" onClick={() => setSearchOpen(true)}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" />
                                <path d="M12 12l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                        <Link to="/contact" className={styles.ctaBtn} onClick={closeMenu}>
                            Get Started
                        </Link>
                    </div>
                </div>
            </div>

            <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
        </nav>
    );
};

export default Navigation;
